import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Portal from './Portal';
import Toast from './Toast';

const ToastContext = createContext(null);

const MAX_VISIBLE = 4;

/**
 * useToast() — imperative API for system notifications.
 *
 *   const toast = useToast();
 *   toast.success('Reservation saved');
 *   toast.error('Could not save', { description: 'Try again in a moment.' });
 *   toast.show({ tone: 'info', title: 'Sync started', duration: 0 });  // sticky
 *   const id = toast.info('Uploading…'); toast.dismiss(id);
 *
 * Every method returns the toast id. Options accept any Toast prop
 * (description, action, icon, duration, tone…).
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be used within a <ToastProvider>.');
  }
  return ctx;
}

/**
 * ToastProvider — mount once near the app root. Renders a fixed viewport via
 * a portal (scoped under .ps-scope) and exposes the imperative API on context.
 *
 * Props:
 *   position: 'top-right'|'top-left'|'bottom-right'|'bottom-left'|'top-center'|'bottom-center'
 *   duration: default auto-dismiss (ms) for non-error toasts
 */
export function ToastProvider({
  children,
  position = 'bottom-right',
  duration = 5000,
}) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((opts = {}) => {
    const id = opts.id ?? `ps-toast-${++idRef.current}`;
    setToasts((prev) => {
      const next = [...prev, { duration, tone: 'neutral', ...opts, id }];
      // keep only the most recent N
      return next.slice(-MAX_VISIBLE);
    });
    return id;
  }, [duration]);

  const api = useMemo(() => {
    const make = (tone, defaults) => (title, opts = {}) =>
      show({ tone, title, ...defaults, ...opts });
    return {
      show,
      dismiss,
      dismissAll: () => setToasts([]),
      success: make('success'),
      error: make('danger', { duration: 8000 }),
      warning: make('warning', { duration: 7000 }),
      info: make('info'),
      neutral: make('neutral'),
    };
  }, [show, dismiss]);

  const top = position.startsWith('top');

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Portal className="ps-scope">
        <div
          className={`ps-toast-viewport ps-toast-viewport--${position}`}
          // newest nearest the screen edge for bottom positions
          style={{ flexDirection: top ? 'column' : 'column-reverse' }}
        >
          {toasts.map((t) => (
            <Toast key={t.id} {...t} onDismiss={dismiss} />
          ))}
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
